import { ONE_DAY_MS } from './cartesianChart'
import type { BucketGranularity } from './bucketSeries'
import { MetricUnit } from './types'

/**
 * Shared value/date formatting for the chart views. The tooltip
 * ({@link ChartTooltipView}) and the screen-reader table ({@link ChartDataTable})
 * both read from the same series registry and must present values identically
 * (UTC dates, percentage vs count), so they format through these helpers.
 */

/** UTC date label; day-level unless `monthly`, which drops the day. */
export function formatChartDate(timestampMs: number, monthly: boolean): string {
  return new Date(timestampMs).toLocaleDateString('en-US', {
    month: 'short',
    ...(monthly ? {} : { day: 'numeric' }),
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * Full date label for a *bucket*'s tooltip header, reading as the period the
 * bucket covers rather than a single day (all UTC):
 * - `day`   → `Jun 8, 2026`
 * - `week`  → `Jun 1 – Jun 7, 2026` (its Monday through Sunday)
 * - `month` → `June 2026`
 */
export function formatBucketDate(
  timestampMs: number,
  granularity: BucketGranularity,
): string {
  if (granularity === 'month') {
    return new Date(timestampMs).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    })
  }
  if (granularity === 'week') {
    const to = timestampMs + 6 * ONE_DAY_MS
    const spansYears =
      new Date(timestampMs).getUTCFullYear() !== new Date(to).getUTCFullYear()
    const fromStr = new Date(timestampMs).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(spansYears ? { year: 'numeric' } : {}),
      timeZone: 'UTC',
    })
    const toStr = new Date(to).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    })
    return `${fromStr} – ${toStr}`
  }
  return formatChartDate(timestampMs, false)
}

/** True when the given UTC timestamps fall in more than one calendar year — the
 *  signal for the axis to spell out the year on each tick (e.g. a comparison
 *  overlay against the prior year, or a range that crosses Jan 1). Pure. */
export function spansMultipleYears(timestamps: number[]): boolean {
  let year: number | undefined
  for (const ts of timestamps) {
    const y = new Date(ts).getUTCFullYear()
    if (year === undefined) year = y
    else if (y !== year) return true
  }
  return false
}

/** Options controlling the year on a bucket's axis-tick label. */
export interface BucketTickOptions {
  /** Append each label's own year (used when the displayed range spans more than
   *  one calendar year — see {@link spansMultipleYears}). */
  showYear?: boolean
  /** Use the compact `'26` year form rather than the full `2026`. Dense axes and
   *  the stacked comparison labels stay compact; a roomy axis can spell it out. */
  compactYear?: boolean
}

/** Year suffix for an axis label: compact `'26`, or the full year — spaced for a
 *  month label (`Mar 2026`), comma'd after a day number (`Mar 5, 2026`). */
function yearSuffix(
  year: number,
  granularity: BucketGranularity,
  compact: boolean,
): string {
  if (compact) return ` '${String(year).slice(-2)}`
  return granularity === 'month' ? ` ${year}` : `, ${year}`
}

/**
 * Compact x-axis tick formatter for a bucket size: a day or the start of a week
 * (`Jun 8`), or a short month (`Jun`). A week bucket is labelled by its start date
 * — a single point reads evenly on the axis (a range box makes the gaps between
 * ticks look lopsided), and the tooltip ({@link formatBucketDate}) still spells out
 * the full period. When `showYear`, each label carries its own year (so a comparison
 * tick reads its own period) — compact (`Jun '26`) unless there's room to spell it out.
 */
export function makeBucketTickFormatter(
  granularity: BucketGranularity,
  { showYear = false, compactYear = true }: BucketTickOptions = {},
): (timestampMs: number) => string {
  const monthly = granularity === 'month'
  return (timestampMs) => {
    const date = new Date(timestampMs)
    const base = date.toLocaleDateString('en-US', {
      month: 'short',
      ...(monthly ? {} : { day: 'numeric' }),
      timeZone: 'UTC',
    })
    return showYear
      ? base + yearSuffix(date.getUTCFullYear(), granularity, compactYear)
      : base
  }
}

/** Percentage values get 2 decimals + `%`; counts get thousands separators. */
export function formatChartValue(value: number, unit?: MetricUnit): string {
  if (unit === MetricUnit.PERCENTAGE) return `${value.toFixed(2)}%`
  return value.toLocaleString()
}
